/**
 * Argus — Admin Updates content (auto-update agent management).
 *
 * Bloc auto-update-agent 2026-05-06.
 * Refactored ca floating window global 2026-05-06 msi-deployment:
 * conținutul tab-urilor exportat ca `UpdatesContent`, mount-uit în `UpdatesWindow.tsx` (Rnd-based).
 * 4 tab-uri: Channels / Manifests / Upload / History.
 */

import { useState, type ChangeEvent } from 'react';
import {
  Box,
  Tabs,
  Tab,
  Stack,
  Paper,
  Typography,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  LinearProgress,
} from '@mui/material';
import {
  useUpdateChannels,
  useUpdateManifests,
  useUpdateHistory,
  useUploadManifestMutation,
  usePromoteMutation,
  useFreezeMutation,
  useRollbackMutation,
} from '../../lib/updates';
import { ApiError } from '../../lib/api';

type Tab = 'channels' | 'manifests' | 'upload' | 'history';

/**
 * UpdatesContent — body 4 tab-uri admin updates. Reusable: mount-uit în floating window.
 */
export function UpdatesContent(): JSX.Element {
  const [tab, setTab] = useState<Tab>('channels');

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Tabs value={tab} onChange={(_, v) => setTab(v as Tab)} sx={{ mb: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Tab value="channels" label="Canale" />
        <Tab value="manifests" label="Manifests" />
        <Tab value="upload" label="Upload" />
        <Tab value="history" label="Istoric" />
      </Tabs>

      <Box sx={{ flex: 1, overflow: 'auto', px: 1 }}>
        {tab === 'channels' && <ChannelsTab />}
        {tab === 'manifests' && <ManifestsTab />}
        {tab === 'upload' && <UploadTab onSuccess={() => setTab('manifests')} />}
        {tab === 'history' && <HistoryTab />}
      </Box>
    </Box>
  );
}

// ============================================================================
// CHANNELS TAB
// ============================================================================

function ChannelsTab(): JSX.Element {
  const channelsQuery = useUpdateChannels();
  const freeze = useFreezeMutation();
  // Hooks at top-level component body (Rules of Hooks). Capturate ca obiecte mutation,
  // apoi `.mutateAsync()` chemate în callback la confirm dialog. NU re-call hook în onConfirm.
  const promoteMutation = usePromoteMutation();
  const rollbackMutation = useRollbackMutation();
  const [rollbackDialog, setRollbackDialog] = useState<{ channel: string } | null>(null);
  const [promoteDialog, setPromoteDialog] = useState<{ channel: string } | null>(null);

  if (channelsQuery.isLoading) return <CircularProgress />;
  if (channelsQuery.isError) return <Alert severity="error">{channelsQuery.error.message}</Alert>;

  return (
    <Stack spacing={3}>
      {channelsQuery.data?.map((c) => (
        <Paper key={c.name} sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Typography variant="h6">{c.name.toUpperCase()}</Typography>
              {c.isFrozen && <Chip label="FROZEN" color="warning" size="small" />}
              {c.currentManifest ? (
                <Chip label={`v${c.currentManifest.version}`} color="primary" size="small" />
              ) : (
                <Chip label="no manifest" size="small" />
              )}
            </Box>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={c.isFrozen}
                    onChange={(e) => freeze.mutate({ channel: c.name, frozen: e.target.checked })}
                    disabled={freeze.isPending}
                  />
                }
                label="Freeze"
              />
              <Button
                variant="outlined"
                size="small"
                onClick={() => setPromoteDialog({ channel: c.name })}
              >
                Promote from another channel
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="warning"
                onClick={() => setRollbackDialog({ channel: c.name })}
                disabled={!c.currentManifest}
              >
                Rollback
              </Button>
            </Box>
          </Box>

          {c.currentManifest && (
            <Typography variant="body2" color="text.secondary">
              Current manifest: {c.currentManifest.version} · {(c.currentManifest.sizeBytes / 1024 / 1024).toFixed(2)} MB
              {c.currentManifest.publishedAt && ` · published ${c.currentManifest.publishedAt}`}
              {c.currentManifest.publishedBy && ` by ${c.currentManifest.publishedBy}`}
            </Typography>
          )}

          <Box sx={{ mt: 2 }}>
            <Typography variant="caption" color="text.secondary">Agents per version:</Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 0.5 }}>
              {Object.entries(c.agentCountPerVersion).map(([version, count]) => (
                <Chip key={version} label={`${version}: ${count}`} size="small" variant="outlined" />
              ))}
              {Object.keys(c.agentCountPerVersion).length === 0 && (
                <Chip label="no agents on channel" size="small" variant="outlined" />
              )}
            </Box>
          </Box>
        </Paper>
      ))}

      {rollbackDialog && (
        <ManifestSelectDialog
          title={`Rollback channel ${rollbackDialog.channel}`}
          confirmLabel="Rollback (force downgrade)"
          confirmColor="warning"
          channelFilter={null}
          onClose={() => setRollbackDialog(null)}
          onConfirm={(manifestId) => {
            void rollbackMutation.mutateAsync({ channel: rollbackDialog.channel, manifestId });
          }}
        />
      )}

      {promoteDialog && (
        <ManifestSelectDialog
          title={`Promote to channel ${promoteDialog.channel}`}
          confirmLabel="Promote"
          confirmColor="primary"
          channelFilter={null}
          onClose={() => setPromoteDialog(null)}
          onConfirm={(manifestId) => {
            void promoteMutation.mutateAsync({ channel: promoteDialog.channel, manifestId });
          }}
        />
      )}
    </Stack>
  );
}

interface ManifestSelectDialogProps {
  title: string;
  confirmLabel: string;
  confirmColor: 'primary' | 'warning';
  channelFilter: string | null;
  onClose: () => void;
  onConfirm: (manifestId: string) => void;
}

function ManifestSelectDialog(props: ManifestSelectDialogProps): JSX.Element {
  const [selected, setSelected] = useState('');
  const manifestsQuery = useUpdateManifests(props.channelFilter, 1);

  return (
    <Dialog open onClose={props.onClose} maxWidth="md" fullWidth>
      <DialogTitle>{props.title}</DialogTitle>
      <DialogContent>
        {manifestsQuery.isLoading && <CircularProgress />}
        {manifestsQuery.data && (
          <FormControl fullWidth sx={{ mt: 1 }}>
            <InputLabel>Manifest</InputLabel>
            <Select value={selected} label="Manifest" onChange={(e) => setSelected(e.target.value)}>
              {manifestsQuery.data.manifests.map((m) => (
                <MenuItem key={m.manifestId} value={m.manifestId}>
                  v{m.version} · {m.channel} · {(m.sizeBytes / 1024 / 1024).toFixed(2)} MB · {m.publishedAt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={props.onClose}>Cancel</Button>
        <Button
          variant="contained"
          color={props.confirmColor}
          disabled={!selected}
          onClick={() => {
            props.onConfirm(selected);
            props.onClose();
          }}
        >
          {props.confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ============================================================================
// MANIFESTS TAB
// ============================================================================

function ManifestsTab(): JSX.Element {
  const [channelFilter, setChannelFilter] = useState<string>('');
  const [page, setPage] = useState(1);
  const query = useUpdateManifests(channelFilter || null, page);

  return (
    <Stack spacing={2}>
      <FormControl size="small" sx={{ width: 200 }}>
        <InputLabel>Channel</InputLabel>
        <Select value={channelFilter} label="Channel" onChange={(e) => setChannelFilter(e.target.value)}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="dev">dev</MenuItem>
          <MenuItem value="staging">staging</MenuItem>
          <MenuItem value="prod">prod</MenuItem>
        </Select>
      </FormControl>

      {query.isLoading && <CircularProgress />}
      {query.isError && <Alert severity="error">{query.error.message}</Alert>}
      {query.data && (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Version</TableCell>
                <TableCell>Channel</TableCell>
                <TableCell>Size</TableCell>
                <TableCell>SHA-256</TableCell>
                <TableCell>Force</TableCell>
                <TableCell>Min</TableCell>
                <TableCell>Published</TableCell>
                <TableCell>By</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {query.data.manifests.map((m) => (
                <TableRow key={m.manifestId}>
                  <TableCell>{m.version}</TableCell>
                  <TableCell><Chip label={m.channel} size="small" /></TableCell>
                  <TableCell>{(m.sizeBytes / 1024 / 1024).toFixed(2)} MB</TableCell>
                  <TableCell><code style={{ fontSize: 11 }}>{m.sha256.slice(0, 16)}…</code></TableCell>
                  <TableCell>{m.isForce ? '✓' : '—'}</TableCell>
                  <TableCell>{m.minSupportedVersion ?? '—'}</TableCell>
                  <TableCell>{m.publishedAt}</TableCell>
                  <TableCell>{m.publishedBy ?? '—'}</TableCell>
                </TableRow>
              ))}
              {query.data.manifests.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center">No manifests</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
          {query.data.pagination && query.data.pagination.totalPages > 1 && (
            <Stack direction="row" spacing={1} sx={{ p: 1, justifyContent: 'flex-end' }}>
              <Button size="small" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>‹</Button>
              <Typography variant="body2" sx={{ alignSelf: 'center' }}>
                {page} / {query.data.pagination.totalPages}
              </Typography>
              <Button size="small" disabled={page >= query.data.pagination.totalPages} onClick={() => setPage(p => p + 1)}>›</Button>
            </Stack>
          )}
        </Paper>
      )}
    </Stack>
  );
}

// ============================================================================
// UPLOAD TAB
// ============================================================================

function UploadTab({ onSuccess }: { onSuccess: () => void }): JSX.Element {
  const [file, setFile] = useState<File | null>(null);
  const [version, setVersion] = useState('');
  const [channel, setChannel] = useState<'dev' | 'staging' | 'prod'>('dev');
  const [minSupported, setMinSupported] = useState('');
  const [isForce, setIsForce] = useState(false);
  const [changelog, setChangelog] = useState('');
  const [progress, setProgress] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);

  const upload = useUploadManifestMutation();

  function handleFile(ev: ChangeEvent<HTMLInputElement>) {
    const f = ev.target.files?.[0] ?? null;
    setFile(f);
    if (f && !version) {
      const m = f.name.match(/argus-agent-(\d+\.\d+\.\d+)\.zip/);
      if (m && m[1]) setVersion(m[1]);
    }
  }

  function handleSubmit() {
    setError(null);
    if (!file) {
      setError('Select a ZIP file');
      return;
    }
    if (!/^\d+\.\d+\.\d+$/.test(version)) {
      setError('Invalid version (X.Y.Z)');
      return;
    }
    setProgress(0);
    const params: Parameters<typeof upload.mutate>[0] = {
      file,
      version,
      channel,
      isForce,
      onProgress: (p: number) => setProgress(p),
    };
    if (minSupported) params.minSupportedVersion = minSupported;
    if (changelog) params.changelog = changelog;
    upload.mutate(
      params,
      {
        onSuccess: () => {
          setProgress(100);
          setFile(null);
          setVersion('');
          setChangelog('');
          setTimeout(() => {
            setProgress(null);
            onSuccess();
          }, 800);
        },
        onError: (err: unknown) => {
          setProgress(null);
          if (err instanceof ApiError) setError(err.messages[0]?.message ?? `HTTP ${err.httpStatus}`);
          else setError(String(err));
        },
      }
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Typography variant="h6">Upload new bundle</Typography>

        <Button variant="outlined" component="label">
          {file ? file.name : 'Select ZIP'}
          <input type="file" accept=".zip" hidden onChange={handleFile} />
        </Button>
        {file && (
          <Typography variant="caption" color="text.secondary">
            {(file.size / 1024 / 1024).toFixed(2)} MB
          </Typography>
        )}

        <TextField
          label="Version (X.Y.Z)"
          value={version}
          onChange={(e) => setVersion(e.target.value)}
          size="small"
          required
        />

        <FormControl size="small">
          <InputLabel>Target channel</InputLabel>
          <Select value={channel} label="Target channel" onChange={(e) => setChannel(e.target.value as 'dev' | 'staging' | 'prod')}>
            <MenuItem value="dev">dev</MenuItem>
            <MenuItem value="staging">staging</MenuItem>
            <MenuItem value="prod">prod</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Min supported version (optional)"
          value={minSupported}
          onChange={(e) => setMinSupported(e.target.value)}
          size="small"
          placeholder="ex: 1.0.0"
        />

        <FormControlLabel
          control={<Switch checked={isForce} onChange={(e) => setIsForce(e.target.checked)} />}
          label="Force update (allow downgrade)"
        />

        <TextField
          label="Changelog (max 5000 chars)"
          value={changelog}
          onChange={(e) => setChangelog(e.target.value)}
          multiline
          rows={4}
          size="small"
        />

        {error && <Alert severity="error">{error}</Alert>}

        {progress !== null && (
          <Box>
            <LinearProgress variant="determinate" value={progress} />
            <Typography variant="caption">{progress}%</Typography>
          </Box>
        )}

        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={upload.isPending || !file}
        >
          Publish manifest
        </Button>
      </Stack>
    </Paper>
  );
}

// ============================================================================
// HISTORY TAB
// ============================================================================

function HistoryTab(): JSX.Element {
  const [statusFilter, setStatusFilter] = useState<string>('');
  const page = 1;

  const filters: { agentId?: string; status?: string } = {};
  if (statusFilter) filters.status = statusFilter;
  const query = useUpdateHistory(filters, page);

  return (
    <Stack spacing={2}>
      <FormControl size="small" sx={{ width: 220 }}>
        <InputLabel>Status</InputLabel>
        <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
          <MenuItem value="">All</MenuItem>
          <MenuItem value="success">success</MenuItem>
          <MenuItem value="failed">failed</MenuItem>
          <MenuItem value="rolled_back">rolled_back</MenuItem>
          <MenuItem value="installing">installing</MenuItem>
        </Select>
      </FormControl>

      {query.isLoading && <CircularProgress />}
      {query.data && (
        <Paper>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Agent</TableCell>
                <TableCell>From</TableCell>
                <TableCell>To</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Error</TableCell>
                <TableCell>Started</TableCell>
                <TableCell>Completed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {query.data.history.map((h) => (
                <TableRow key={h.id}>
                  <TableCell>{h.hostname ?? h.agentId.slice(0, 8)}</TableCell>
                  <TableCell>{h.fromVersion ?? '—'}</TableCell>
                  <TableCell>{h.toVersion}</TableCell>
                  <TableCell>
                    <Chip
                      label={h.status}
                      size="small"
                      color={
                        h.status === 'success' ? 'success' :
                        h.status === 'failed' ? 'error' :
                        h.status === 'rolled_back' ? 'warning' : 'default'
                      }
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" sx={{ maxWidth: 200, display: 'inline-block' }} title={h.errorMessage ?? ''}>
                      {h.errorMessage?.slice(0, 60) ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell>{h.startedAt}</TableCell>
                  <TableCell>{h.completedAt ?? '—'}</TableCell>
                </TableRow>
              ))}
              {query.data.history.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center">No history</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Stack>
  );
}
